import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import SiteLayout from "./components/SiteLayout";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Portfolio from "./pages/Portfolio";
import ProjectDetail from "./pages/ProjectDetail";
import Products from "./pages/Products";
import Contact from "./pages/Contact";
import ThankYou from "./pages/ThankYou";
import HolidayHomes from "./pages/HolidayHomes";
import CurtainStyleGuide from "./pages/CurtainStyleGuide";
import CurtainsDubai from "./pages/CurtainsDubai";
import CurtainsDubaiCompany from "./pages/CurtainsDubaiCompany";
import InteriorFitOutDubai from "./pages/InteriorFitOutDubai";
import InteriorFitOutAbuDhabi from "./pages/InteriorFitOutAbuDhabi";
import InteriorFitOutSharjah from "./pages/InteriorFitOutSharjah";
import VillaRenovationDubai from "./pages/VillaRenovationDubai";
import OfficeFitOutDubai from "./pages/OfficeFitOutDubai";
import TurnkeyContractorUAE from "./pages/TurnkeyContractorUAE";
import KitchenRenovationDubai from "./pages/KitchenRenovationDubai";
import CustomWardrobesDubai from "./pages/CustomWardrobesDubai";
import CustomSofasDubai from "./pages/CustomSofasDubai";
import CustomJoineryDubai from "./pages/CustomJoineryDubai";
import EmailSignature from "./pages/EmailSignature";
import Blog from "./pages/Blog";
import BlogArticle from "./pages/BlogArticle";
import OngoingProjects from "./pages/OngoingProjects";
import OngoingProjectDetail from "./pages/OngoingProjectDetail";
import InteriorFitOutCompanyDubai from "./pages/InteriorFitOutCompanyDubai";
import FitOutCalculator from "./pages/FitOutCalculator";
import NotFound from "./pages/NotFound";
import SpamGone from "./pages/SpamGone";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import { PortalAuthProvider, usePortalAuth } from "./contexts/PortalAuthContext";
import { PortalLanguageProvider } from "./lib/PortalLanguageContext";
import PortalLogin from "./pages/portal/PortalLogin";
import PortalDashboard from "./pages/portal/PortalDashboard";
import ProjectPage from "./pages/portal/ProjectPage";
import UsersAccessPage from "./pages/portal/UsersAccessPage";
import PortalErrorBoundary from "./components/portal/PortalErrorBoundary";
import { captureUtms, captureAttribution, trackWhatsAppClick, loadWaConfig, applyWaRouting } from "./lib/waTracker";
import { trackEvent, GTM_ID } from "./lib/analytics";
import CrmLogin from "./pages/crm/CrmLogin";
import CrmLayout from "./pages/crm/CrmLayout";
import LibraryPage from "./pages/crm/LibraryPage";
import SalesOpsPage from "./pages/crm/SalesOpsPage";
import CrmDashboard from "./pages/crm/CrmDashboard";
import LeadsPage from "./pages/crm/LeadsPage";
import LeadDetailPage from "./pages/crm/LeadDetailPage";
import ConversationsPage from "./pages/crm/ConversationsPage";
import TasksPage from "./pages/crm/TasksPage";
import StaffPage from "./pages/crm/StaffPage";
import KnowledgePage from "./pages/crm/KnowledgePage";
import CrmSettingsPage from "./pages/crm/CrmSettingsPage";
import ReportsPage from "./pages/crm/ReportsPage";
import InsightsPage from "./pages/crm/InsightsPage";

// Optional GTM bootstrap. Only loads when REACT_APP_GTM_ID is provided at
// build time — otherwise the site runs fully with tracking disabled and events
// simply accumulate on window.dataLayer (harmless if never consumed).
function bootstrapGtm() {
  try {
    if (!GTM_ID || typeof document === "undefined") return;
    if (document.getElementById("gtm-loader")) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    const s = document.createElement("script");
    s.id = "gtm-loader";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(s);
  } catch {
    /* analytics must never break the site */
  }
}

// Query-spam guard: historical spam followed ?i=...&channel=...&from=...
// When all three appear together, serve the truthful removal page instead of
// real content. Legitimate UTM / gclid / fbclid params are untouched.
function SpamQueryGuard({ children }) {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  if (p.get("i") && p.get("channel") && p.get("from")) return <SpamGone />;
  return children;
}

function App() {
  // Listens once at document level and auto-detects any `wa.me` link so every
  // WhatsApp CTA emits a single, consistent `whatsapp_click` event.
  useEffect(() => {
    // Initialize the event queue even when no GTM/GA id is configured so
    // instrumented events are observable and queue safely for later vendor
    // activation. Network loading stays gated on a configured id.
    window.dataLayer = window.dataLayer || [];
    captureUtms();
    captureAttribution();
    loadWaConfig();
    bootstrapGtm();
    const handler = (e) => {
      const anchor = e.target.closest && e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (!/^https?:\/\/(?:api\.)?wa\.me\//i.test(href)) return;

      // Temporary routing: rewrite destination number before navigation.
      applyWaRouting(anchor);

      trackEvent("whatsapp_click", {
        cta_location: anchor.getAttribute("data-testid") || "wa-link",
      });
      trackWhatsAppClick(anchor);
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <SpamQueryGuard>
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:key" element={<ServiceDetail />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:id" element={<ProjectDetail />} />
            <Route path="/ongoing-projects" element={<OngoingProjects />} />
            <Route path="/ongoing-projects/:slug" element={<OngoingProjectDetail />} />
            <Route path="/products" element={<Products />} />
            <Route path="/holiday-homes" element={<HolidayHomes />} />
            <Route path="/curtains-dubai" element={<CurtainsDubai />} />
            <Route path="/curtains-dubai/styles/:slug" element={<CurtainStyleGuide />} />
            <Route path="/curtains-dubai-company" element={<CurtainsDubaiCompany />} />
            <Route path="/interior-fit-out-dubai" element={<InteriorFitOutDubai />} />
            <Route path="/interior-fit-out-abu-dhabi" element={<InteriorFitOutAbuDhabi />} />
            <Route path="/interior-fit-out-sharjah" element={<InteriorFitOutSharjah />} />
            <Route path="/villa-renovation-dubai" element={<VillaRenovationDubai />} />
            <Route path="/office-fit-out-dubai" element={<OfficeFitOutDubai />} />
            <Route path="/turnkey-contractor-uae" element={<TurnkeyContractorUAE />} />
            <Route path="/kitchen-renovation-dubai" element={<KitchenRenovationDubai />} />
            <Route path="/custom-wardrobes-dubai" element={<CustomWardrobesDubai />} />
            <Route path="/custom-sofas-dubai" element={<CustomSofasDubai />} />
            <Route path="/custom-joinery-dubai" element={<CustomJoineryDubai />} />
            <Route path="/email-signature" element={<EmailSignature />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route
              path="/interior-fit-out-company-dubai"
              element={<InteriorFitOutCompanyDubai />}
            />
            <Route path="/contact" element={<Contact />} />
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/fit-out-cost-calculator-dubai" element={<FitOutCalculator />} />
            {/* Truthful removal pages for known spam URL patterns (see SpamGone.jsx) */}
            <Route path="/c/*" element={<SpamGone />} />
            <Route path="/portfolio-items/*" element={<SpamGone />} />
            <Route path="/tschatelnyj-razbor-gemblingplatformy-s-vozmozhnostju-demorezhima" element={<SpamGone />} />
            <Route path="/tschatelnyj-razbor-gemblingplatformy-s-vozmozhnostju-demorezhima/*" element={<SpamGone />} />
            {/* Branded 404 — never silently render the homepage for unknown URLs */}
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          {/* AI Sales CRM — internal, auth-gated */}
          <Route path="/crm/login" element={<CrmLogin />} />
          <Route path="/admin/crm" element={<CrmLayout />}>
            <Route index element={<CrmDashboard />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/:leadId" element={<LeadDetailPage />} />
            <Route path="conversations" element={<ConversationsPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="staff" element={<StaffPage />} />
            <Route path="knowledge" element={<KnowledgePage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="sales-ops" element={<SalesOpsPage />} />
            <Route path="settings" element={<CrmSettingsPage />} />
            <Route path="insights" element={<InsightsPage />} />
            <Route path="reports" element={<ReportsPage />} />
          </Route>
          {/* Private Project Portal — auth-gated, role-aware */}
          <Route
            path="/projects/*"
            element={
              <PortalErrorBoundary>
                <PortalAuthProvider>
                  <PortalLanguageBridge>
                    <Routes>
                      <Route index element={<PortalLogin />} />
                      <Route path="admin" element={<PortalDashboard variant="admin" />} />
                      <Route path="users" element={<UsersAccessPage />} />
                      <Route path="manager" element={<PortalDashboard variant="manager" />} />
                      <Route path="site" element={<PortalDashboard variant="site" />} />
                      <Route path="my-projects" element={<PortalDashboard variant="my" />} />
                      <Route path=":slug" element={<ProjectPage />} />
                    </Routes>
                  </PortalLanguageBridge>
                </PortalAuthProvider>
              </PortalErrorBoundary>
            }
          />
        </Routes>
        </SpamQueryGuard>
      </BrowserRouter>
    </div>
  );
}

// Bridges the authenticated user's preferred_language into the
// PortalLanguageProvider so it can hydrate on first paint.
function PortalLanguageBridge({ children }) {
  const { user } = usePortalAuth();
  return <PortalLanguageProvider user={user}>{children}</PortalLanguageProvider>;
}

export default App;
